import React, { isValidElement, type JSX } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogTitleProps,
} from "@mui/material";
import {alpha, styled} from "@mui/material/styles";
import {CheckCircleRounded, ErrorRounded, InfoRounded, WarningRounded} from "@mui/icons-material";

/** Visual severity of the dialog — mirrors MUI Alert's `severity` prop. */
export type AcknowledgmentDialogSeverity = 'info' | 'warning' | 'error' | 'success';

// One icon per severity. Adding a new severity means adding one entry here —
// the styled title/icon below already read colors generically from
// `theme.palette[severity]`, so no other changes are needed to scale this up.
const SEVERITY_ICONS: Record<AcknowledgmentDialogSeverity, typeof InfoRounded> = {
  info: InfoRounded,
  warning: WarningRounded,
  error: ErrorRounded,
  success: CheckCircleRounded,
};

const DialogActionsGrey = styled(DialogActions)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
}));

// Mimics MUI Alert's severity treatment: a light color wash behind
// severity-colored text, since this dialog is purely informational.
const SeverityDialogTitle = styled(DialogTitle, {
  shouldForwardProp: (prop) => prop !== 'severity',
})<{ severity: AcknowledgmentDialogSeverity }>(({theme, severity}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  backgroundColor: alpha(theme.palette[severity].main, 0.1),
  color: theme.palette[severity].dark,
}));

const SeverityIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'severity',
})<{ severity: AcknowledgmentDialogSeverity }>(({theme, severity}) => ({
  display: 'flex',
  color: theme.palette[severity].main,
}));

interface AcknowledgmentDialogProps {
  body: string | JSX.Element;
  /** Called when the user dismisses the dialog — via the dismiss button, ESC, or backdrop click. */
  onClose: () => void;
  open: boolean;
  title: string | JSX.Element;
  titleProps?: Partial<DialogTitleProps>;
  /** Label for the single dismiss button. Defaults to "OK". */
  dismissText?: string;
  /** Controls the title's icon and color treatment. Defaults to "info". */
  severity?: AcknowledgmentDialogSeverity;
}

/**
 * A single-action dialog for acknowledging a warning or notice — no Cancel/Confirm
 * choice, since there's nothing to confirm. Dismissible via its button, ESC, or
 * backdrop click (all routed through `onClose`). The title uses an Alert-style
 * severity treatment (light color wash + icon), same set of severities as MUI Alert.
 * Next-step actions (e.g. a primary CTA) belong in `body` alongside the message,
 * not in the footer — pass them as part of the `body` JSX.
 */
const AcknowledgmentDialog = (
  {
    open = false,
    onClose,
    title,
    body,
    titleProps = {variant: 'h6'},
    dismissText = "OK",
    severity = 'info',
  }: AcknowledgmentDialogProps) => {
  const SeverityIconComponent = SEVERITY_ICONS[severity];
  const instanceId = React.useId();
  const titleId = `ss-acknowledge-title-${instanceId}`;
  const descriptionId = `ss-acknowledge-description-${instanceId}`;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <SeverityDialogTitle id={titleId} severity={severity} {...titleProps}>
        <SeverityIcon severity={severity}>
          <SeverityIconComponent fontSize="large" />
        </SeverityIcon>
        {title}
      </SeverityDialogTitle>
      <DialogContent
        id={descriptionId}
        sx={{
          px: 4,
          py: 3,
          '&&': { pt: 3 },
        }}
      >
        {isValidElement(body) ? <>{body}</>
          : <DialogContentText>{body}</DialogContentText>
        }
      </DialogContent>
      <DialogActionsGrey>
        <Button color={'primary'} onClick={onClose} autoFocus>
          {dismissText}
        </Button>
      </DialogActionsGrey>
    </Dialog>
  );
};

export default AcknowledgmentDialog;



